"use client";

import { useEffect, useLayoutEffect, useRef } from "react";

import { analytics } from "@/lib/analytics/analytics";
import { AnalyticsEvents } from "@/lib/analytics/events";
import { identifyAnalyticsUser } from "@/lib/analytics/identify-user";
import { isPostHogUserExcluded } from "@/lib/analytics/runtime";
import { consumeOAuthAnalyticsCookie } from "@/lib/analytics/oauth-analytics-cookie";
import {
  clearAnalyticsSessionUserId,
  setAnalyticsSessionUserId,
} from "@/lib/analytics/session-user";
import {
  useCurrentUser,
  type CurrentUser,
} from "@/hooks/queries/useCurrentUser";

function fireOAuthEvents(profile: CurrentUser): void {
  if (!analytics.isEnabled()) return;

  const oauth = consumeOAuthAnalyticsCookie();
  if (!oauth) return;

  if (oauth.isNew) {
    if (oauth.provider === "google") {
      analytics.capture(AnalyticsEvents.oauthSignupGoogle);
      analytics.capture(AnalyticsEvents.accountCreated, {
        signup_method: "oauth",
        auth_provider: "google",
      });
    } else {
      analytics.capture(AnalyticsEvents.oauthSignupGithub);
      analytics.capture(AnalyticsEvents.accountCreated, {
        signup_method: "oauth",
        auth_provider: "github",
      });
    }
  } else {
    analytics.capture(AnalyticsEvents.loginSuccess, {
      signup_method: "oauth",
      auth_provider: oauth.provider,
    });
  }

  identifyAnalyticsUser(profile);
}

/** Binds codex user id to analytics guards; identifies non-excluded users only. */
export function PostHogAuthSync() {
  const { data: user, isSuccess } = useCurrentUser();
  const identifiedRef = useRef<string | null>(null);

  useLayoutEffect(() => {
    if (!isSuccess || !user?.id) {
      clearAnalyticsSessionUserId();
      return;
    }

    setAnalyticsSessionUserId(user.id);
  }, [isSuccess, user?.id]);

  useEffect(() => {
    if (!isSuccess || !user?.id) return;
    if (isPostHogUserExcluded(user.id)) return;

    fireOAuthEvents(user);

    if (identifiedRef.current === user.id) return;
    identifiedRef.current = user.id;
    identifyAnalyticsUser(user);
  }, [isSuccess, user]);

  return null;
}
